import { useId, useState, type ReactElement, type ReactNode } from 'react';
import { CalendarPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import type {
  CancelledProspectTour,
  CompletedProspectTour,
  PropertyTourBookableWindow,
  ProspectTourCalendarTextRecipient,
} from '../../services/tenantApi';
import { formatEasternClock } from '../../utils/eastern-week';
import { formatTourBedBath, formatTourHomeLabel, listBookedTours, type BookedTour } from '../../utils/tour-home';
import { downloadBookedTourCalendar } from '../../utils/tour-ics';

type TourTableStatus = 'upcoming' | 'completed' | 'canceled';

interface TourTableRow {
  bookingId: string;
  status: TourTableStatus;
  building: boolean;
  imageUrl: string | null;
  unitNumber: string;
  propertyName: string;
  bedrooms: number;
  bathrooms: number;
  startAt: string;
  endAt: string;
  unitId: string | null;
}

const HEADER_CELL =
  'sticky top-0 z-10 border-b border-gray-200 bg-white px-1.5 py-2 text-left text-[11px] font-medium uppercase tracking-normal text-gray-400 dark:border-white/10 dark:bg-gray-800 dark:text-gray-500';

const BODY_CELL = 'overflow-hidden border-b border-gray-100 dark:border-white/5 px-1.5 py-2.5 align-middle text-gray-700 dark:text-gray-300';

const ANALYZE_BUTTON_CLASS =
  'inline-flex items-center justify-center rounded-lg border border-red-600 px-2.5 py-1.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/50';

/** Room for table header plus at least two tour rows so the card stays scrollable on short viewports. */
const TOURS_TABLE_MIN_HEIGHT = 'min-h-[11.5rem]';

function formatTableDate(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

function buildTourRows(
  upcoming: BookedTour[],
  completedTours: CompletedProspectTour[],
  cancelledTours: CancelledProspectTour[],
): TourTableRow[] {
  return [
    ...upcoming.map(
      (tour): TourTableRow => ({
        bookingId: tour.bookingId,
        status: 'upcoming',
        building: tour.building,
        imageUrl: tour.imageUrl,
        unitNumber: tour.unitNumber,
        propertyName: tour.propertyName,
        bedrooms: tour.bedrooms,
        bathrooms: tour.bathrooms,
        startAt: tour.startAt,
        endAt: tour.endAt,
        unitId: null,
      }),
    ),
    ...completedTours.map(
      (tour): TourTableRow => ({
        bookingId: tour.bookingId,
        status: 'completed',
        building: tour.building,
        imageUrl: tour.imageUrl,
        unitNumber: tour.unitNumber,
        propertyName: tour.propertyName,
        bedrooms: tour.bedrooms,
        bathrooms: tour.bathrooms,
        startAt: tour.startAt,
        endAt: tour.endAt,
        unitId: tour.unitId,
      }),
    ),
    ...cancelledTours.map(
      (tour): TourTableRow => ({
        bookingId: tour.bookingId,
        status: 'canceled',
        building: tour.building,
        imageUrl: null,
        unitNumber: tour.unitNumber,
        propertyName: tour.propertyName,
        bedrooms: tour.bedrooms,
        bathrooms: tour.bathrooms,
        startAt: tour.startAt,
        endAt: tour.endAt,
        unitId: null,
      }),
    ),
  ];
}

interface BookedToursCardProps {
  windows: PropertyTourBookableWindow[];
  completedTours: CompletedProspectTour[];
  cancelledTours: CancelledProspectTour[];
  groupName: string;
  calendarTextRecipients: ProspectTourCalendarTextRecipient[];
  busy: boolean;
  onCancel: (bookingId: string) => void;
  onTextCalendar: (bookingIds: string[], audience?: 'self' | 'group') => Promise<void>;
  className?: string;
}

export default function BookedToursCard({
  windows,
  completedTours,
  cancelledTours,
  groupName,
  calendarTextRecipients,
  busy,
  onCancel,
  onTextCalendar,
  className = '',
}: BookedToursCardProps): ReactElement {
  const tours = listBookedTours(windows);
  const rows = buildTourRows(tours, completedTours, cancelledTours);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [textPhase, setTextPhase] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [textError, setTextError] = useState<string | null>(null);
  const selectedRows = rows.filter((row) => selectedIds.includes(row.bookingId));
  const selectedTours = selectedRows
    .filter((row) => row.status !== 'canceled')
    .map((row) => rowToBookedTour(row));
  const selectedUpcoming = selectedRows.filter((row) => row.status === 'upcoming');
  const hasSelectedTours = selectedTours.length > 0;
  const downloadHelp = hasSelectedTours
    ? 'Save a calendar file (.ics) on this device for the selected tours. Open it in Apple Calendar, Google Calendar, Outlook, or another calendar app to add those tour times.'
    : 'Select one or more tours in the table below. Download saves a .ics file on this device—you can open it in Apple Calendar, Google Calendar, Outlook, or another app.';
  const canTextGroup = calendarTextRecipients.length > 0;
  const textTooltipContent = hasSelectedTours ? (
    <span className="block space-y-2">
      <span className="block">
        Texts everyone in {groupName} who has a phone on file. Each person gets the selected tour details
        (Eastern time), a note that you scheduled the tour, and a calendar file they can add on their phone.
      </span>
      {canTextGroup ? (
        <span className="block">
          <span className="block font-semibold text-gray-200 dark:text-gray-100">Will text:</span>
          <ul className="mt-1 list-inside list-disc space-y-0.5">
            {calendarTextRecipients.map((recipient) => (
              <li key={recipient.personId}>{recipient.displayName}</li>
            ))}
          </ul>
        </span>
      ) : (
        <span className="block">No one in your group has a phone on file yet, so texting is unavailable.</span>
      )}
    </span>
  ) : (
    'Select one or more tours in the table below. Text sends the calendar invite to everyone in your group who has a phone on file.'
  );

  const toggleRow = (bookingId: string) => {
    setTextPhase('idle');
    setTextError(null);
    setSelectedIds((current) =>
      current.includes(bookingId) ? current.filter((id) => id !== bookingId) : [...current, bookingId],
    );
  };

  const textSelected = () => {
    if (textPhase === 'sending' || selectedTours.length === 0) return;
    setTextPhase('sending');
    setTextError(null);
    void onTextCalendar(selectedTours.map((tour) => tour.bookingId), 'group')
      .then(() => setTextPhase('sent'))
      .catch((err: unknown) => {
        setTextPhase('error');
        setTextError(
          err instanceof Error
            ? err.message
            : 'We could not send the calendar text. Try Download to save a .ics file on this device.',
        );
      });
  };

  return (
    <section
      className={`flex shrink-0 flex-col rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 px-4 py-4 shadow-bento sm:px-6 sm:py-5 ${className}`}
    >
      <div>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Tours</h2>
        <p className="mt-1 max-w-xl text-sm text-gray-500 dark:text-gray-400">
          {tours.length > 0
            ? `Upcoming first, then completed, then canceled. Upcoming tours are shared with ${groupName}.`
            : 'Upcoming first, then completed, then canceled.'}
        </p>
      </div>
      {rows.length === 0 ? (
        <p className="mt-4 rounded-xl bg-gray-50 dark:bg-white/5 px-4 py-5 text-sm text-gray-500 dark:text-gray-400">
          Tours you book show up here until the visit is over.
        </p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={busy || selectedUpcoming.length === 0}
                onClick={() => selectedUpcoming.forEach((row) => onCancel(row.bookingId))}
                className="rounded-lg border border-red-600 px-3 py-2 text-sm font-semibold text-red-700 disabled:opacity-40"
              >
                Cancel appointment
              </button>
            </div>
            <div className="ml-auto flex max-w-full flex-col items-end gap-2">
              <h3 className="text-sm font-semibold text-gray-900 underline decoration-gray-400 underline-offset-4 dark:text-white dark:decoration-gray-500">
                Add to Calendar
              </h3>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <InstantActionTooltip content={textTooltipContent}>
                  <button
                    type="button"
                    disabled={!hasSelectedTours || !canTextGroup || textPhase === 'sending'}
                    onClick={textSelected}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
                  >
                    <CalendarPlus className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {textPhase === 'sent' ? 'Texts sent' : textPhase === 'sending' ? 'Sending…' : 'Text'}
                  </button>
                </InstantActionTooltip>
                <InstantActionTooltip content={downloadHelp}>
                  <button
                    type="button"
                    disabled={!hasSelectedTours}
                    onClick={() => downloadBookedTourCalendar(selectedTours, groupName)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-600 px-3 py-2 text-sm font-semibold text-red-700 disabled:opacity-40 dark:text-red-400"
                  >
                    <CalendarPlus className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Download
                  </button>
                </InstantActionTooltip>
              </div>
              {textError ? (
                <p className="max-w-sm text-right text-xs leading-snug text-rose-700 dark:text-rose-300">{textError}</p>
              ) : null}
            </div>
          </div>
          <div className={`mt-3 overflow-x-hidden ${TOURS_TABLE_MIN_HEIGHT}`}>
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className={`${HEADER_CELL} w-8`} scope="col">
                  <span className="sr-only">Select</span>
                </th>
                <th className={`${HEADER_CELL} w-10 px-0`} scope="col">
                  <span className="sr-only">Photo</span>
                </th>
                <th className={`${HEADER_CELL} whitespace-nowrap`} scope="col">
                  Home
                </th>
                <th className={`${HEADER_CELL} whitespace-nowrap`} scope="col">
                  Date
                </th>
                <th className={`${HEADER_CELL} whitespace-nowrap`} scope="col">
                  Time
                </th>
                <th className={HEADER_CELL} scope="col">
                  Analyze
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const canceled = row.status === 'canceled';
                const home = formatTourHomeLabel(row.unitNumber, row.propertyName, row.building);
                return (
                  <tr key={row.bookingId} className={canceled ? 'text-gray-500 dark:text-gray-400' : undefined}>
                    <td className={`${BODY_CELL} w-8`}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.bookingId)}
                        onChange={() => toggleRow(row.bookingId)}
                        aria-label={`Select ${home}`}
                      />
                    </td>
                    <td className={`${BODY_CELL} w-10 px-0`}>
                      {row.status !== 'canceled' && row.imageUrl ? (
                        <span className="block h-10 w-full overflow-hidden rounded-md bg-gray-100 dark:bg-gray-700">
                          <img src={row.imageUrl} alt="" className="h-full w-full object-cover" />
                        </span>
                      ) : null}
                    </td>
                    <th
                      className={`${BODY_CELL} overflow-hidden text-left font-medium ${
                        canceled ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-white'
                      }`}
                      scope="row"
                    >
                      <span className="block whitespace-nowrap">{home}</span>
                      {!row.building ? (
                        <span className="mt-0.5 block whitespace-nowrap text-xs font-normal text-gray-500 dark:text-gray-400">
                          {formatTourBedBath(row.bedrooms, row.bathrooms)}
                        </span>
                      ) : null}
                    </th>
                    <td className={`${BODY_CELL} whitespace-nowrap`}>{formatTableDate(row.startAt)}</td>
                    <td className={`${BODY_CELL} whitespace-nowrap ${canceled ? 'line-through' : ''}`}>
                      {formatEasternClock(row.startAt, true)}
                    </td>
                    <td className={`${BODY_CELL} whitespace-nowrap`}>
                      {row.status === 'completed' && row.unitId ? (
                        <Link
                          to={`/analyze-units?unit=${encodeURIComponent(row.unitId)}`}
                          className={`whitespace-nowrap ${ANALYZE_BUTTON_CLASS}`}
                        >
                          Analyze
                        </Link>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </section>
  );
}

function InstantActionTooltip({ content, children }: { content: ReactNode; children: ReactNode }) {
  const tooltipId = useId();
  return (
    <span className="group relative inline-flex has-[:disabled]:cursor-not-allowed">
      <span aria-describedby={tooltipId}>{children}</span>
      <span
        id={tooltipId}
        role="tooltip"
        className="pointer-events-none invisible absolute bottom-[calc(100%+0.5rem)] right-0 z-50 w-[min(100vw-2rem,20rem)] rounded-lg border border-gray-200 bg-gray-900 px-3 py-2.5 text-left text-xs font-normal leading-relaxed text-white shadow-lg ring-1 ring-black/5 group-focus-within:visible group-hover:visible dark:border-white/10 dark:bg-gray-950 dark:text-gray-100 dark:ring-white/10"
      >
        {content}
        <span
          className="absolute -bottom-1.5 right-4 h-3 w-3 rotate-45 border-b border-r border-gray-200 bg-gray-900 dark:border-white/10 dark:bg-gray-950"
          aria-hidden="true"
        />
      </span>
    </span>
  );
}

function rowToBookedTour(row: TourTableRow): BookedTour {
  return {
    bookingId: row.bookingId,
    building: row.building,
    unitNumber: row.unitNumber,
    propertyName: row.propertyName,
    bedrooms: row.bedrooms,
    bathrooms: row.bathrooms,
    imageUrl: row.imageUrl,
    startAt: row.startAt,
    endAt: row.endAt,
  };
}
